import React from 'react';
import useScrollReveal, { useStaggerReveal } from '../hooks/useScrollReveal';

const PROJECT_CATEGORIES = [
  {
    id: 'production-ml',
    title: 'Production ML Systems',
    description: 'End-to-end machine learning systems deployed to production',
    color: 'from-teal-500 to-cyan-500',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    projects: [
      {
        title: 'Runway Friction & Closure Prediction (RFCPS)',
        description: 'Built an ML system that predicts when airport runways become unsafe due to ice or snow, helping operations teams at Minneapolis and Denver airports make real-time closure decisions. Combined multiple ML models into a stacking ensemble and delivered predictions via automated feeds and a live web dashboard.',
        tags: ['Stacking Ensemble', 'Neural Networks', 'GBT', 'Real-time'],
        metric: '2 major airports served',
      },
      {
        title: 'National Water Model Calibration (IWAA)',
        description: 'Developed an ML workflow for the USGS to identify which environmental factors most influence streamflow predictions across the U.S. Analyzed 42 years of hydrological data to calibrate the National Water Model, improving flood and drought forecasting accuracy for 1,500+ river basins.',
        tags: ['Feature Importance', 'Calibration', 'HPC', 'Hydrology'],
        metric: '1,500+ basins calibrated',
      },
      {
        title: 'Kuwait Renewable Energy Forecasting',
        description: 'Created ML training datasets for a solar and wind energy forecasting system used by Kuwait\'s energy sector to optimize renewable power generation. Performed verification analysis that was published in the peer-reviewed Energies journal.',
        tags: ['Solar Forecasting', 'Wind Forecasting', 'Verification'],
        metric: 'Published in Energies',
      },
    ],
  },
  {
    id: 'systems-architecture',
    title: 'Systems & Architecture',
    description: 'Large-scale refactoring and architectural modernization',
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    projects: [
      {
        title: 'DICast Microservices Migration',
        description: 'Modernized NCAR\'s flagship weather forecasting engine (DICast) from a single monolithic application into independent, containerized microservices. This made the system easier to scale, deploy, and maintain — enabling faster feature delivery for commercial weather clients. Built REST APIs and deployed on AWS.',
        tags: ['Docker', 'Flask', 'Swagger', 'AWS ECS', 'Microservices'],
        metric: 'Monolith to microservices',
      },
      {
        title: 'Climate Data Pipeline Refactoring',
        description: 'Redesigned the 65 TB data pipeline that ingests real-time weather observations (radar, satellite, forecast models) and feeds them into the National Water Model. Refactored legacy code into a clean object-oriented architecture and introduced automated testing, reducing bugs and speeding up onboarding for new developers.',
        tags: ['OOP', 'Python', 'pytest', 'Data Pipelines'],
        metric: '65 TB processed',
      },
    ],
  },
  {
    id: 'cloud-infrastructure',
    title: 'Cloud & Infrastructure',
    description: 'AWS cloud platforms and HPC-to-cloud workflows',
    color: 'from-orange-500 to-amber-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
    projects: [
      {
        title: 'Cloud Training Platform',
        description: 'Built an AWS-based platform that lets scientists worldwide run hydrological simulations through their browser — no local setup required. Used at major scientific conferences (AGU, AMS) and university workshops, enabling 150+ researchers to train simultaneously on NCAR\'s flood forecasting model.',
        tags: ['AWS', 'SaaS', 'JupyterHub', 'WRF-Hydro'],
        metric: '150+ simultaneous users',
      },
      {
        title: 'Idaho Cloud Seeding Assessment',
        description: 'Deployed containerized weather simulations on AWS for the Idaho Department of Water Resources to evaluate whether cloud seeding (injecting particles into clouds) effectively increases snowpack and water supply. Compressed what would have been months of compute time into under 2 days through parallelized cloud workflows.',
        tags: ['AWS EC2', 'Docker', 'HPC', 'Parallel Computing'],
        metric: '20-year sim in <2 days',
      },
    ],
  },
  {
    id: 'cross-platform',
    title: 'Cross-Platform Integration',
    description: 'Bridging incompatible systems, formats, and agency workflows',
    color: 'from-blue-500 to-indigo-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    ),
    projects: [
      {
        title: 'NASA Drought Monitor Integration',
        description: 'Adapted NCAR\'s calibration tools to run on NASA\'s supercomputers, enabling NASA to improve the accuracy of their drought monitoring system. Bridged two incompatible computing environments with different configurations and job schedulers — a cross-agency integration that hadn\'t been done before.',
        tags: ['NASA', 'HPC', 'WRF-Hydro', 'Cross-Agency'],
        metric: '2 HPC platforms bridged',
      },
      {
        title: 'National Water Model — Cloud-Optimized Data',
        description: 'Converted decades of National Water Model output (flood predictions, streamflow data) into a modern cloud-friendly format (Zarr), making it possible for researchers to analyze water data for Puerto Rico, Hawaii, the continental U.S., and Alaska directly from the cloud — without downloading terabytes of files.',
        tags: ['Zarr', 'NWM', 'Data Conversion', 'Cloud-Optimized'],
        metric: '4 regions converted',
      },
      {
        title: 'METplus — Weather Model Verification',
        description: 'Contributed to a unified framework that measures how accurate weather forecast models are. Used by NOAA, the U.S. Air Force, the Naval Research Lab, and international weather agencies to standardize how they evaluate forecast quality — bridging the gap between research and real-world operations.',
        tags: ['METplus', 'NOAA', 'USAF', 'NRL', 'Verification'],
        metric: '4+ agencies served',
      },
    ],
  },
];

const ProjectCard = ({ project, category }) => (
  <div className={`${category.bgColor}/60 rounded-2xl border ${category.borderColor} shadow-sm hover:shadow-lg transition-all duration-300 p-5`}>
    <h4 className="text-base font-bold text-gray-900 mb-2">
      {project.title}
    </h4>
    <p className="text-sm text-gray-600 mb-3 leading-relaxed">
      {project.description}
    </p>
    <div className="mb-3">
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${category.color} text-white`}>
        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
        {project.metric}
      </span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {project.tags.map((tag) => (
        <span
          key={tag}
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${category.bgColor} text-gray-700 border ${category.borderColor}`}
        >
          {tag}
        </span>
      ))}
    </div>
  </div>
);

const ProjectShowcase = () => {
  const [headerRef, headerVisible] = useScrollReveal();
  const [categoriesRef, , getCategoryItemProps] = useStaggerReveal(
    PROJECT_CATEGORIES.length,
    { staggerDelay: 0.15 }
  );

  return (
    <section id="projects" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-emerald-200/30 to-teal-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-40 right-10 w-96 h-96 bg-gradient-to-br from-orange-200/20 to-amber-200/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto relative">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 text-sm font-medium mb-4">
            Engineering Portfolio
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Project <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Showcase</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Production systems built across ML, cloud infrastructure, and scientific computing at NCAR
          </p>
        </div>

        {/* Category Groups */}
        <div ref={categoriesRef} className="space-y-12">
          {PROJECT_CATEGORIES.map((category, catIndex) => {
            const catItemProps = getCategoryItemProps(catIndex);
            return (
              <div key={category.id} className={catItemProps.className} style={catItemProps.style}>
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-r ${category.color} text-white`}>
                    {category.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{category.title}</h3>
                    <p className="text-sm text-gray-500">{category.description}</p>
                  </div>
                </div>

                {/* Project Cards Grid */}
                <div className={`grid gap-4 ${category.projects.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-xl'}`}>
                  {category.projects.map((project) => (
                    <ProjectCard key={project.title} project={project} category={category} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProjectShowcase;
